import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";
import { useAppStore } from "@/lib/store";
import type { TestProject } from "@/features/tests/TestCard";

interface TestApplyDialogProps {
  project: TestProject;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  lockThresholdUsd?: number;
}

export function TestApplyDialog({
  project,
  open,
  onClose,
  onSuccess,
  lockThresholdUsd = 8,
}: TestApplyDialogProps) {
  if (!project) return null;
  const user = useAppStore((s) => s.user);
  const initAuth = useAppStore((s) => s.initAuth);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (open && !user) {
      initAuth();
    }
  }, [open, user, initAuth]);

  const handleUnlockPayment = async () => {
    if (!user) {
      window.location.href = "/login";
      return;
    }
    setIsProcessing(true);
    try {
      const { initiateIntaSendPayment } = await import("@/lib/api");
      const result = await initiateIntaSendPayment({
        amount: 2,
        currency: "USD",
        email: user.email ?? "",
        metadata: {
          description: "One-time support fee to unlock tests",
          type: "maintenance_fee",
        },
      });

      if (result.success && result.authorizationUrl) {
        window.location.href = result.authorizationUrl;
      } else {
        toast.error(result.message || "Failed to initialize payment");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Payment initialization failed";
      toast.error(message);
      console.error("Payment error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-lg font-bold">Unlock All Tests</DialogTitle>
          <DialogDescription className="text-center text-xs">{project.title}</DialogDescription>
        </DialogHeader>

        <div className="text-center space-y-4 py-2">
          <div className="space-y-1">
            <p className="text-sm font-medium text-slate-700">One-Time Support Fee</p>
            <p className="text-2xl font-bold text-emerald-600">$2</p>
          </div>

          <p className="text-sm text-slate-600 leading-relaxed">
            Thanks for being part of our testing community 🙏
            <br />
            We're a small team keeping this platform running. A one-time $2 support fee covers
            server costs and development. This is not a subscription — pay once and everything stays
            unlocked forever.
            <br />
            <span className="font-medium">We really appreciate your support 💙</span>
          </p>

          <div className="flex items-center justify-center gap-2 pt-1 text-xs text-slate-500">
            <Lock className="h-3 w-3" />
            <span>Secure payment via IntaSend</span>
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          <Button
            className="w-full h-11 bg-blue-600 hover:bg-green-600 text-white font-medium"
            onClick={handleUnlockPayment}
            disabled={isProcessing}
          >
            {isProcessing ? (
              "Processing..."
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Lock className="h-4 w-4" />
                Pay $2 to Unlock All Tests
              </span>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
